import { formatDataType, type DataItemId } from '@pn/core/domain/data';
import { getMapFeatureIds, type MapFeature } from '@pn/core/domain/map';
import {
  createWorkspaceItem,
  getColorComponents,
  type WorkspaceItem,
} from '@pn/core/domain/workspace';
import { STREAMING_LIMIT } from '@pn/core/limits';
import { useNavigation } from '@pn/core/operations/dataSelection';
import {
  applyDynamicStyling,
  getDefaultBinCount,
  getDefaultGradientColorPalette,
  getDefaultRadiusRange,
  getDefaultStaggeredColors,
} from '@pn/core/operations/workspace/hooks/useDynamicStyling';
import {
  useCurrentUserStorage,
  useWorkspaceStorage,
  workspaceActions,
} from '@pn/core/storage';
import { assert } from '@pn/core/utils/assert';
import { muiColorPalette } from '@pn/services/color/colorPalettes';
import { notificationService } from '@pn/services/notifications';
import { isArray, isEmpty, isNil, uniq } from 'lodash-es';
import React from 'react';

export function useMapSelectionProcessor() {
  const { workspaceItemSelected, workspaceItems } = useWorkspaceStorage();

  const dataTypeSelected = workspaceItemSelected?.dataType;
  const sourceItemIds = React.useMemo(
    () =>
      Object.values(workspaceItems)
        .filter((item) => isNil(item.sourceItem))
        .map(({ id }) => id),
    [workspaceItems]
  );

  const processSelection = useProcessSelection();

  /**
   * Builds a new selection under a new data key without affecting other queries.
   */
  const processMixedMapSelection = React.useCallback(
    (params: { features: MapFeature[]; append: boolean }): void => {
      const { features, append } = params;

      const featuresByDataType = features.reduce<Record<string, MapFeature[]>>(
        (acc, feature) => {
          if (isNil(acc[feature.dataType])) {
            acc[feature.dataType] = [];
          }
          acc[feature.dataType].push(feature);
          return acc;
        },
        {}
      );

      const selectedMapFeatureIds = !isNil(dataTypeSelected)
        ? getMapFeatureIds(featuresByDataType[dataTypeSelected] ?? [])
        : [];

      if (isNil(dataTypeSelected) || isEmpty(selectedMapFeatureIds)) {
        for (let i = sourceItemIds.length - 1; i >= 0; i--) {
          const sourceItemId = sourceItemIds[i];

          const mapFeatureIds = getMapFeatureIds(
            featuresByDataType[sourceItemId] ?? []
          );

          if (mapFeatureIds.length > STREAMING_LIMIT) {
            return notificationService.warning(
              `You can only select up to ${STREAMING_LIMIT.toLocaleString()} ${formatDataType(
                sourceItemId
              )}`
            );
          }

          if (!isEmpty(mapFeatureIds)) {
            return processSelection({
              featureIds: mapFeatureIds,
              sourceItemId,
              append,
            });
          }
        }
      } else {
        if (selectedMapFeatureIds.length > STREAMING_LIMIT) {
          return notificationService.warning(
            `You can only select up to ${STREAMING_LIMIT.toLocaleString()} ${formatDataType(
              dataTypeSelected
            )}`
          );
        }

        processSelection({
          featureIds: selectedMapFeatureIds,
          sourceItemId: dataTypeSelected,
          append,
        });
      }
    },
    [dataTypeSelected, sourceItemIds, processSelection]
  );

  return { processMixedMapSelection };
}

/**
 * If append mode is specified, the value can be true or false.
 * In exclusion mode, the value must be true.
 */
type ProcessSelectionParams = {
  featureIds: DataItemId[];
  sourceItemId: string;
} & (
  | {
      append: boolean;
      exclude?: never;
    }
  | {
      append?: never;
      exclude: true;
    }
);

/**
 * Used for both map selection and map clicks in append mode.
 */
export function useProcessSelection() {
  const { user } = useCurrentUserStorage();
  const { allWorkspaceItems, workspaceItemSelected } = useWorkspaceStorage();

  const { navigateTo } = useNavigation();

  return React.useCallback(
    (params: ProcessSelectionParams): void => {
      const { featureIds, sourceItemId, append, exclude } = params;

      const selectionItem: WorkspaceItem = (() => {
        const existingTemporaryItem =
          allWorkspaceItems[getSelectionItemId(sourceItemId)];

        if (
          !isNil(existingTemporaryItem) &&
          !isNil(existingTemporaryItem.query.scopeIds)
        ) {
          const scopeIds = exclude
            ? existingTemporaryItem.query.scopeIds.filter(
                (id: DataItemId) => !featureIds.includes(id)
              )
            : append
              ? uniq([...existingTemporaryItem.query.scopeIds, ...featureIds])
              : featureIds;

          return {
            ...existingTemporaryItem,
            numberOfElements: scopeIds.length,
            query: {
              ...existingTemporaryItem.query,
              scopeIds,
            },
            isProcessed: false,
            isRendered: false,
          };
        }

        const sourceItem = allWorkspaceItems[sourceItemId];

        assert(user, 'User must be defined when processing selection');
        assert(sourceItem, 'Selection source item not found');

        const newItem = createWorkspaceItem(
          {
            source: 'item',
            sourceItem,
            temporarySourceItemId: workspaceItemSelected?.id,
            id: getSelectionItemId(sourceItemId),
            extraStyle: {
              color: muiColorPalette.getMultiSelectionColor(),
            },
            queryOptions: {
              scopeIds: featureIds,
            },
          },
          user
        );

        return newItem;
      })();

      workspaceActions().add(selectionItem);
      workspaceActions().addToWorkspace(selectionItem.id);
      workspaceActions().select(selectionItem.id);
      workspaceActions().updatePage(selectionItem.id, 0);

      navigateTo({ dataType: undefined, id: undefined });

      const layer = selectionItem.map.layers[0];
      const colorComponents = getColorComponents(layer.style.color);

      if (!isEmpty(colorComponents)) {
        applyDynamicStyling({
          item: selectionItem,
          field: colorComponents.field,
          staggeredColors: getDefaultStaggeredColors(),
          gradientColorPalette: getDefaultGradientColorPalette(
            colorComponents.type,
            colorComponents.legend
          ),
          desiredBinCount: getDefaultBinCount(
            colorComponents.type,
            colorComponents.legend
          ),
          radiusRange: getDefaultRadiusRange(layer.style.radius),
          isBubbleMapEnabled: isArray(layer.style.radius),
          wasBubbleMapEnabled: isArray(layer.style.radius),
          updateAll: true,
        });
      }
    },
    [navigateTo, user, allWorkspaceItems, workspaceItemSelected]
  );
}

export function getSelectionItemId(dataType: string): string {
  return `mixed_selection_${dataType}`;
}

export function isSelectionItemId(arg: string): boolean {
  return arg.startsWith('mixed_selection_');
}
